import { useRef, useState } from 'react';
import { useDispatch, useReduxState } from 'hooks';
import api from 'api';
import { useApiData } from 'lib/api';
import { actions } from 'store';
import { getMessageWithPlaceholders } from 'shared-locales';
import {
  ContainerWithShowAll,
  ModalButtonWithForm,
  User
} from 'components/common';
import { EmailMessageEditor } from 'components/common/form';

import type { FormApi, FormValues } from 'informed';
import type { EmailTemplateData, Group, UserWithBasicData } from 'shared-types';

interface Props {
  emailTemplates: EmailTemplateData[];
  emailTemplatesDefault: EmailTemplateData;
  group: Pick<Group, 'id' | 'title'> & {
    totalNumberOfUsers?: number;
  };
  updateGroups: () => Promise<void>;
}

const ShareGroupsSendInvitation = ({
  emailTemplates,
  emailTemplatesDefault,
  group,
  updateGroups
}: Props) => {
  const formApi = useRef<FormApi<FormValues>>(undefined);
  const { dispatch } = useDispatch();
  const [showEmailMessageEditor, setShowEmailMessageEditor] = useState(false);

  const {
    messages,
    pageState: { course }
  } = useReduxState();

  const { data: groupUsersData, loading: groupUsersLoading } = useApiData<{
    users: UserWithBasicData[];
  }>({
    onlyIf: showEmailMessageEditor && group.totalNumberOfUsers > 0,
    url: `/admin/courses/${course.id}/group-users?groupId=${group.id}`
  });

  const groupUsers = groupUsersData?.users || [];

  const initialValues = {
    email: {
      ...emailTemplatesDefault.email,
      selectedTemplateIdentifier: emailTemplatesDefault.identifier
    }
  };

  // send invitation email to all users of the group
  const handleSubmit = async (values: typeof initialValues) => {
    try {
      const response = await api.post(
        `/admin/courses/${course.id}/groups-invite`,
        {
          emailContent: values.email,
          groupId: group.id
        }
      );

      let emailMessage = `${response.data.sentEmails} ${messages.commonSendEmailInvitesSuccess}`;
      let emailMessageIsWarning = false;

      const errorEmails = Object.keys(response.data.errors || {});
      if (errorEmails.length) {
        emailMessageIsWarning = true;
        emailMessage += `<br /><br /> ${messages.commonSendEmailInvitesError}`;
        errorEmails.forEach((errorEmail) => {
          emailMessage += `<br />${errorEmail}`;
        });
      }

      await updateGroups();

      dispatch(
        actions.addNotice({
          text: emailMessage,
          type: emailMessageIsWarning ? 'warning' : 'success'
        })
      );

      formApi.current?.reset();
      return true;
    } catch (err) {
      await dispatch.handleErrorDispatch(err);
      return false;
    }
  };

  const buttonDisabled = group.totalNumberOfUsers < 1;

  return (
    <ModalButtonWithForm
      apiRef={formApi}
      buttonDisabled={buttonDisabled}
      buttonIcon="paper-plane"
      buttonIsDropdown={true}
      buttonText={messages.commonSendInviteEmail}
      initialValues={initialValues}
      modalActionCategory="success"
      modalActionText={messages.commonSend}
      modalTitle={messages.commonSendInviteEmail}
      modalWidth="wide"
      onHide={() => setShowEmailMessageEditor(false)}
      onShow={() => setShowEmailMessageEditor(true)}
      onSubmit={handleSubmit}
    >
      {() => (
        <>
          {showEmailMessageEditor ? (
            <ContainerWithShowAll
              items={groupUsers.map((user) => (
                <div key={user.id} style={{ width: 'max-content' }}>
                  <User user={user} />
                </div>
              ))}
              loading={groupUsersLoading}
              title={getMessageWithPlaceholders({
                message: messages.usersNumberOfAccounts,
                placeholders: {
                  numberOfAccounts: groupUsers.length
                }
              })}
            />
          ) : null}

          <EmailMessageEditor
            availableTemplates={emailTemplates}
            id={`share-groups-send-email-${group.id}`}
            style={{ marginTop: 20 }}
          />
        </>
      )}
    </ModalButtonWithForm>
  );
};

export default ShareGroupsSendInvitation;
